import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MoonIcon, SettingsIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { powerLocationById } from '../../data/power';
import { CONFIDENCE_LABEL, confirmedLabel, countdownLabel, journeyVerdict } from '../../utils/power';
import { POWER_STATE_LABEL, POWER_SOURCE_LABEL } from '../../data/power';
import { ReportPowerSheet } from '../../components/power/ReportPowerSheet';
import { powerIcons } from '../../components/power/PowerPrimitives';
import type { PowerState } from '../../types/power';
const NIGHT_ART = 'https://cdn.magicpatterns.com/patterns/generated-images/9837b041-f2b1-4ec7-972c-0ac7d531ed70.jpg';

/**
 * Before you go, full screen.
 *
 * This is the surface a student opens while standing up to leave, often at night,
 * so it is authored dark rather than inverted: one destination, one status, one
 * countdown, and an honest alternative if the answer is bad. It reads its own
 * colours instead of the theme tokens because it is a deliberately dark scene in
 * both appearances — the same way a map or a photograph is.
 */
export function BeforeYouGoScreen() {
  const {
    next,
    getLocationStatus,
    homeLocationId,
    campusLocationId,
    reports
  } = usePower();
  const [reporting, setReporting] = useState(false);
  const destinationId = next?.locationId ?? campusLocationId;
  const destination = powerLocationById(destinationId);
  const status = getLocationStatus(destinationId);
  const verdict = journeyVerdict([getLocationStatus(homeLocationId), getLocationStatus(campusLocationId), status]);
  const reportCount = reports.filter(item => item.locationId === destinationId).length;
  const stateTone: Record<PowerState, string> = {
    on: 'bg-[#0f5132] text-[#d4f4e2]',
    off: 'bg-[#5c1a17] text-[#fbd5d1]',
    unstable: 'bg-[#5a3a10] text-[#fbe4c4]',
    backup: 'bg-[#12305e] text-[#d3e4fd]',
    unknown: 'bg-white/10 text-white/70'
  };
  const Zap = powerIcons.power;
  return <div className="min-h-screen bg-[#0a1020] text-white" data-id="element-10284" data-mp="1brmnjiy02n3d">
      <div className="mx-auto w-full max-w-2xl px-4 pb-10 pt-4" data-id="element-10285" data-mp="16rh42q1vw6wd">
        <div className="flex items-center justify-between" data-id="element-10286" data-mp="1kpymc890sr6w">
          <Link to="/power" className="flex min-h-touch items-center gap-2 rounded-lg px-2 text-body font-semibold text-white/90 transition-colors duration-150 ease-out hover:text-white" data-id="element-10287" data-mp="1kri2lyuof28o">
            <ArrowLeftIcon className="h-5 w-5" aria-hidden data-id="element-10288" data-mp="1um1o3czymntb" />
            Before you go
          </Link>
          <Link to="/settings" className="flex h-11 w-11 items-center justify-center rounded-lg text-white/70 transition-colors duration-150 ease-out hover:text-white" data-id="element-10289" data-mp="23goply2i6g0a">
            <SettingsIcon className="h-5 w-5" aria-hidden data-id="element-10290" data-mp="2devu3hflv5le" />
            <span className="sr-only" data-id="element-10291" data-mp="2dfe0h6vzf071">Power notification settings</span>
          </Link>
        </div>

        <div className="relative mt-4 overflow-hidden rounded-2xl" data-id="element-10292" data-mp="28hm6867v71zo">
          <img src={NIGHT_ART} alt="A student walking across campus at night" className="h-40 w-full object-cover sm:h-52" data-id="element-10293" data-mp="2s2meefgodjmp" />
          <MoonIcon className="absolute right-4 top-4 h-5 w-5 text-white/70" aria-hidden data-id="element-10294" data-mp="2i3ndxk62cuas" />
        </div>

        {next ? <div className="mt-5" data-id="element-10295" data-mp="2n0na93d42iop">
            <p className="text-body-sm text-white/60" data-id="element-10296" data-mp="29b3921z6hthi">Your next class</p>
            <p className="text-body-lg font-semibold text-white" data-id="element-10297" data-mp="2990b8vrsaw28">
              {next.courseCode} · {next.title}
            </p>
            <p className="text-body-sm text-white/60" data-id="element-10298" data-mp="1zd5obogj2etn">{next.venue}</p>
          </div> : <p className="mt-5 text-body text-white/70" data-id="element-10299" data-mp="24bqppr7in4jo">
            No further classes are scheduled this week, so this is your campus status.
          </p>}

        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.06] p-5" data-id="element-10300" data-mp="1qd0pw3tbaqrm">
          <div className="flex items-start justify-between gap-3" data-id="element-10301" data-mp="1udb05so503ca">
            <p className="text-h4 font-semibold text-white" data-id="element-10302" data-mp="1naw0he5h7qcs">
              {destination?.name ?? 'Your destination'}
            </p>
            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-caption font-semibold uppercase ${stateTone[status.state]}`} data-id="element-10303" data-mp="1icbas8nuop2l">
              <Zap className="h-3.5 w-3.5" aria-hidden data-id="element-10304" data-mp="18jvrn80thug4" />
              {status.state === 'unknown' ? 'No data' : POWER_STATE_LABEL[status.state]}
            </span>
          </div>
          <p className="mt-2 text-body-sm text-white/70" data-id="element-10305" data-mp="zjqjgvnsus0w">
            {CONFIDENCE_LABEL[status.confidence]}
          </p>
          <p className="cz-num text-body-sm text-white/60" data-id="element-10306" data-mp="uns05mhbw07c">
            {confirmedLabel(status.lastConfirmedAt)}
          </p>
          <p className="mt-2 text-caption text-white/50" data-id="element-10307" data-mp="2p0hv87jfnfgb">
            {reportCount} report{reportCount === 1 ? '' : 's'} on record ·{' '}
            {POWER_SOURCE_LABEL[status.source]}
          </p>
          {status.conflicting && <p className="mt-3 rounded-lg bg-[#5a3a10] px-3 py-2 text-caption text-[#fbe4c4]" data-id="element-10308" data-mp="2she0thwr5qb8">
              Recent reports disagree here. Treat this as uncertain.
            </p>}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-5 py-4" data-id="element-10309" data-mp="31aqzl0hhzm58">
          <div data-id="element-10310" data-mp="29jb5kldm2x4z">
            <p className="text-caption uppercase tracking-wide text-white/50" data-id="element-10311" data-mp="29jtbk9q2spq3">Starts in</p>
            <p className="cz-num text-h2 font-semibold leading-tight text-white" data-id="element-10312" data-mp="2dk3flgub562b">
              {next ? next.laterInWeek ? `${next.day} ${next.start}` : countdownLabel(next.startsInMinutes) : '—'}
            </p>
          </div>
          <p className={`rounded-full px-3 py-1.5 text-body-sm font-semibold ${verdict.tone === 'ok' ? 'bg-[#0f5132] text-[#d4f4e2]' : verdict.tone === 'warn' ? 'bg-[#5a3a10] text-[#fbe4c4]' : 'bg-white/10 text-white/70'}`} data-id="element-10313" data-mp="3tz4ydigeumhu">
            {verdict.message}
          </p>
        </div>

        <div className="mt-5 space-y-2.5" data-id="element-10314" data-mp="3j8jnfqr9az9p">
          <Link to="/power/map" className="flex min-h-touch w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-body font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-700" data-id="element-10315" data-mp="3j7i0anwyumbm">
            View power details
            <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-10316" data-mp="2llt04xxhaei9" />
          </Link>
          <Link to="/power/nearby" className="flex min-h-touch w-full items-center justify-center rounded-xl border border-white/20 px-4 text-body font-medium text-white transition-colors duration-150 ease-out hover:bg-white/10" data-id="element-10317" data-mp="30a7cbzgsbfbh">
            See alternatives
          </Link>
          <button type="button" onClick={() => setReporting(true)} className="flex min-h-touch w-full items-center justify-center rounded-xl px-4 text-body-sm font-medium text-white/70 transition-colors duration-150 ease-out hover:text-white" data-id="element-10318" data-mp="22sytqsdx7upn">
            Report what you can see
          </button>
        </div>
      </div>

      <ReportPowerSheet open={reporting} onClose={() => setReporting(false)} presetLocationId={destinationId} data-id="element-10319" data-mp="dc3cb5wqhv25" />
    </div>;
}